import React, { useState, useEffect } from "react";
import { Document, ArrowDown2, ArrowUp2, Folder, DocumentDownload, CloseCircle } from "iconsax-react";

import DevelopersLogin from "../../../assets/images/developers_login.png";
import ResourceDownloadsSidebar from "../../../assets/images/resource_downloads_sidebar.png";

const CourseworkLayout = () => {
  // State for expanding/collapsing folders
  const [expandedItems, setExpandedItems] = useState({});
  
  // State for password protection
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedDownload, setSelectedDownload] = useState(null);
  const [downloads, setDownloads] = useState([]);

  // Map of asset filenames to their imported references
  const assetMap = {
    "DevelopersLogin.png": DevelopersLogin,
    "ResourceDownloadsSidebar.png": ResourceDownloadsSidebar,
    // Add more mappings as needed
  };

  // Function to get the actual resource based on type and path
  const getResource = (type, path) => {
    if (!type || !path) return null;
    
    if (type === 'asset') {
      return assetMap[path] || null;
    } else if (type === 'external') {
      return path;
    } else if (type === 'comingsoon') {
      return null;
    }
    return null;
  };

  // Environment-based password mapping
  const passwordMapping = {
    1: process.env.REACT_APP_DOWNLOAD_PASSWORD_SANDBOX,
    2: process.env.REACT_APP_DOWNLOAD_PASSWORD_STAGING,
    3: process.env.REACT_APP_DOWNLOAD_PASSWORD_PRODUCTION,
    4: process.env.REACT_APP_DOWNLOAD_PASSWORD_BUILDING_BLOCKS,
    5: process.env.REACT_APP_DOWNLOAD_PASSWORD_POLYFINTECH,
    6: process.env.REACT_APP_DOWNLOAD_PASSWORD_COURSEINSTRUCTOR,
    7: process.env.REACT_APP_DOWNLOAD_PASSWORD_OPENAPIPUBLISHINGPROCESS
  };

  // Function to extract item configuration for an environment
  const getEnvironmentItems = (envName, envId) => {
    const items = [];
    let itemIndex = 1;
    
    // Continue reading items until we don't find any more
    while (true) {
      const nameKey = `REACT_APP_DOWNLOAD_${envName}_ITEM_${itemIndex}_NAME`;
      const name = process.env[nameKey];
      
      // Break if we don't find any more items
      if (!name) break;
      
      const typeKey = `REACT_APP_DOWNLOAD_${envName}_ITEM_${itemIndex}_TYPE`;
      const pathKey = `REACT_APP_DOWNLOAD_${envName}_ITEM_${itemIndex}_PATH`;
      const filenameKey = `REACT_APP_DOWNLOAD_${envName}_ITEM_${itemIndex}_FILENAME`;
      const passwordKey = `REACT_APP_DOWNLOAD_${envName}_ITEM_${itemIndex}_PASSWORD`;
      
      const type = process.env[typeKey];
      const path = process.env[pathKey];
      const filename = process.env[filenameKey];
      const itemPassword = process.env[passwordKey];
      
      const url = getResource(type, path);
      
      items.push({
        id: parseInt(`${envId}0${itemIndex}`), // Creates IDs like 101, 102, 201, etc.
        name,
        url,
        filename,
        type,
        password: itemPassword,
        passwordProtected: !!itemPassword && type !== 'comingsoon'
      });
      
      itemIndex++;
    }
    
    return items;
  };

  // Initialize downloads from environment variables
  useEffect(() => {
    // Get all Building Blocks items
    const allBuildingBlocksItems = getEnvironmentItems('BUILDING_BLOCKS', 4);
    
    // Create Web folder (initially empty)
    const webFolder = {
      id: 401,
      name: "Web",
      isFolderGroup: true,
      subItems: allBuildingBlocksItems.filter(item => 
        item.id === 403 
      )
    };
    
    // Create Mobile folder with Flutter item (item ID 402)
    const mobileFolder = {
      id: 402,
      name: "Mobile",
      isFolderGroup: true,
      subItems: allBuildingBlocksItems.filter(item => 
        item.id === 402 // Direct mapping to Flutter item by ID
      )
    };
    
    // Create Backend folder with Backend API item (item ID 401)
    const backendFolder = {
      id: 403,
      name: "Backend",
      isFolderGroup: true,
      subItems: allBuildingBlocksItems.filter(item => 
        item.id === 401 // Direct mapping to Backend item by ID
      )
    };

    // Get all PolyFinTech items
    const allPolyFinTechItems = getEnvironmentItems('POLYFINTECH', 5);
    
    // Create PayTech folder (items 1-4: NETS QR Web, eNETS Web, NETS Click Lite, NETS QR Mobile)
    const payTechFolder = {
      id: 501,
      name: "PayTech",
      isFolderGroup: true,
      subItems: allPolyFinTechItems.filter(item => 
        [501, 502, 503, 504].includes(item.id) // Items 1-4
      )
    };
    
    // Create UAT folder (items 5-7: UAT items 5, 6, 7)
    const uatFolder = {
      id: 502,
      name: "UAT",
      isFolderGroup: true,
      subItems: allPolyFinTechItems.filter(item => 
        [505, 506, 507, 508].includes(item.id) // Items 5-7
      )
    };
    
    // Create Go-Live folder (item 8: Production Deployment Guide)
    const goLiveFolder = {
      id: 503,
      name: "Go Live",
      isFolderGroup: true,
      subItems: allPolyFinTechItems.filter(item => 
        [509].includes(item.id) // Item 8 (Production Deployment Guide)
      )
    };

    const downloadData = [
      {
        id: 1, 
        name: "Building Blocks",
        subItems: [webFolder, mobileFolder, backendFolder] // Three folders instead of direct items
      },
      {
        id: 2,
        name: "Sandbox Environment",
        subItems: getEnvironmentItems('SANDBOX', 1)
      },
      {
        id: 3,
        name: "Staging Environment",
        subItems: getEnvironmentItems('STAGING', 2)
      },
      {
        id: 4,
        name: "Production Environment",
        subItems: getEnvironmentItems('PRODUCTION', 3)
      },
      {
        id: 5, 
        name: "PolyFinTech",
        subItems: [payTechFolder, uatFolder, goLiveFolder] // Three folders like Building Blocks
      },
      {
        id: 6,
        name: "Course Instructor",
        subItems: getEnvironmentItems('COURSEINSTRUCTOR', 6)
      },
      {
        id: 7,
        name: "Open API Publishing Process",
        subItems: getEnvironmentItems('OPENAPIPUBLISHINGPROCESS', 7)
      }
    ];
    
    setDownloads(downloadData);
  }, []);

  const toggleItem = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };
  
  // Handle download button click
  const handleDownloadClick = (e, subItem, parentItem) => {
    e.preventDefault();
    
    // Skip for coming soon items
    if (subItem.type === 'comingsoon' || !subItem.url) {
      return;
    }
    
    // Check if the individual item requires password protection
    if (subItem.passwordProtected && subItem.url) {
      // Store the selected download, then open password modal
      setSelectedDownload(subItem);
      setPassword("");
      setErrorMessage("");
      setShowPasswordModal(true);
    } else if (subItem.url) {
      // For non-protected items with valid URLs, download directly
      initiateDownload(subItem);
    }
  };
  
  // Process download after password verification or for non-protected files
  const initiateDownload = (item) => {
    const link = document.createElement('a');
    link.href = item.url;
    link.download = item.filename || item.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Handle password submission
  const handlePasswordSubmit = () => {
    // Get the expected password from the selected item
    const expectedPassword = selectedDownload.password;
    
    // Check if password matches
    if (password === expectedPassword) {
      initiateDownload(selectedDownload);
      setShowPasswordModal(false);
    } else {
      setErrorMessage("Incorrect password. Please try again.");
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-800">Coursework</h1>
      </div>

      <div className="space-y-4">
        {downloads.map((item) => (
          <div key={item.id} className="mb-6">
            {/* Main Item Header - Now with Folder icon */}
            <div
              className="flex items-center justify-between cursor-pointer bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              onClick={() => toggleItem(item.id)}
            >
              <div className="flex items-center">
                <Folder size={24} className="text-blue-600 mr-4" />
                <h2 className="text-lg font-semibold text-gray-800">
                  {item.name}
                </h2>
              </div>
              {expandedItems[item.id] ? (
                <ArrowUp2 size={20} className="text-gray-600" />
              ) : (
                <ArrowDown2 size={20} className="text-gray-600" />
              )}
            </div>

            {/* Sub-Items - Handle both folder groups and regular items */}
            {expandedItems[item.id] && item.subItems && item.subItems.length > 0 && (
              <ul className="mt-2 space-y-2 ml-6">
                {item.subItems.map((subItem) => (
                  <li key={subItem.id}>
                    {subItem.isFolderGroup ? (
                      // Render folder group (Web, Mobile, Backend for Building Blocks; PayTech, UAT, Go-Live for PolyFinTech)
                      <div>
                        <div 
                          className="flex items-center justify-between cursor-pointer bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                          onClick={() => toggleItem(subItem.id)}
                        >
                          <div className="flex items-center">
                            <Folder size={20} className="text-blue-500 mr-3" />
                            <span className="text-gray-800 font-medium">
                              {subItem.name}
                            </span>
                          </div>
                          {expandedItems[subItem.id] ? (
                            <ArrowUp2 size={18} className="text-gray-600" />
                          ) : (
                            <ArrowDown2 size={18} className="text-gray-600" />
                          )}
                        </div>

                        {/* Folder contents */}
                        {expandedItems[subItem.id] && subItem.subItems && (
                          <ul className="mt-2 space-y-2 ml-6">
                            {subItem.subItems.length > 0 ? (
                              subItem.subItems.map((nestedItem) => (
                                <li
                                  key={nestedItem.id}
                                  className="flex items-center justify-between bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                                >
                                  <div className="flex items-center">
                                    <Document size={20} className="text-blue-600 mr-3" />
                                    <span className="text-gray-800 font-medium">
                                      {nestedItem.name}
                                    </span>
                                  </div>
                                  <div className="flex items-center">
                                    {nestedItem.url ? (
                                      <button
                                        className="text-blue-600 hover:text-blue-800 p-2 rounded-full hover:bg-blue-50 transition-colors cursor-pointer"
                                        onClick={(e) => handleDownloadClick(e, nestedItem, item)}
                                        onContextMenu={(e) => e.preventDefault()}
                                        title="Download"
                                      >
                                        <DocumentDownload size={20} className="text-blue-600" />
                                      </button>
                                    ) : (
                                      <span className="text-gray-400 italic px-2 py-2">Coming Soon</span>
                                    )}
                                  </div>
                                </li>
                              ))
                            ) : (
                              <li className="p-3 text-gray-500 italic">
                                No items available
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                    ) : (
                      // Regular item rendering (for other sections) - now matching the nested item styling
                      <div className="flex items-center justify-between bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center">
                          <Document size={20} className="text-blue-600 mr-3" />
                          <span className="text-gray-800 font-medium">
                            {subItem.name}
                          </span>
                        </div>
                        <div className="flex items-center">
                          {subItem.url ? (
                            <button
                              className="text-blue-600 hover:text-blue-800 p-2 rounded-full hover:bg-blue-50 transition-colors cursor-pointer"
                              onClick={(e) => handleDownloadClick(e, subItem, item)}
                              onContextMenu={(e) => e.preventDefault()}
                              title="Download"
                            >
                              <DocumentDownload size={20} className="text-blue-600" />
                            </button>
                          ) : (
                            <span className="text-gray-400 italic px-2 py-2">Coming Soon</span>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Protected Download</h3>
              <button 
                onClick={() => setShowPasswordModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <CloseCircle size={20} />
              </button>
            </div>
            
            <p className="mb-4 text-sm text-gray-600">
              <strong>{selectedDownload?.name}</strong> requires a password to download.
            </p>
            
            {errorMessage && (
              <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
                {errorMessage}
              </div>
            )}
            
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded mb-4 text-black"
              placeholder="Enter password"
              onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
              autoFocus
              style={{ WebkitTextSecurity: 'disc' }} 
            />
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseworkLayout;